import React, { useState } from "react";
import { Avatar, Button, Modal, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Cropper from "react-easy-crop";
import { useDispatch } from "react-redux";
import { getData, uploadFile } from "../../actions/apiCall";
import { putData } from "../../actions/developer";
import { checkAuth, getAccess } from "../../actions/auth";
import { useSelector } from "react-redux";

const MAX_CROP_SIZE = 500;

const ProfileAvatar = () => {
    const [isCropModalVisible, setCropModalVisible] = useState(false);
    const [uploadedImage, setUploadedImage] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
    const { user } = useSelector((state: any) => state?.app);

    const dispatch = useDispatch();

    const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            setUploadedImage(URL.createObjectURL(file));
            setCropModalVisible(true);
        }
    };

    const onCropComplete = (_croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const cropImage = async () => {
        if (uploadedImage && croppedAreaPixels) {
            try {
                const croppedBlob = await getCroppedImg(uploadedImage, croppedAreaPixels);

                // Convert Blob to File
                const croppedFile = new File([croppedBlob], "cropped-image.jpg", {
                    type: "image/jpeg",
                });

                const formData = new FormData();
                formData.append("file", croppedFile);

                const resData = await uploadFile("/upload/add/single", formData);

                if (resData.status === "SUCCESS") {
                    const updateProfile = await dispatch(putData(`/users/${user?._id}`, { avatar: resData.file.url }));
                    if (updateProfile) {
                        await dispatch(checkAuth());
                    }
                    message.success("Image uploaded successfully!");
                } else {
                    throw new Error("Invalid response from server");
                }
            } catch (error) {
                console.error("Error uploading image:", error);
                message.error("Failed to upload image. Please try again.");
            }
            setCropModalVisible(false);
        }
    };

    const getCroppedImg = async (imageSrc: string, crop: any): Promise<Blob> => {
        const image = await createImage(imageSrc);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        const cropWidth = Math.min(crop.width, MAX_CROP_SIZE);
        const cropHeight = Math.min(crop.height, MAX_CROP_SIZE);

        canvas.width = cropWidth;
        canvas.height = cropHeight;

        if (ctx) {
            ctx.drawImage(
                image,
                crop.x * scaleX,
                crop.y * scaleY,
                crop.width * scaleX,
                crop.height * scaleY,
                0,
                0,
                cropWidth,
                cropHeight
            );
        }

        return new Promise((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    if (blob) resolve(blob);
                    else reject(new Error("Failed to create cropped image blob"));
                },
                "image/jpeg",
                1 // Quality of the output image
            );
        });
    };

    const createImage = (url: string) =>
        new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = (error) => reject(error);
            img.src = url;
        });

    return (
        <div style={{ textAlign: "center" }} >
            <Avatar
                src={`${process.env.FILE_URL}${user?.avatar}`}
                size={150}
                style={{ border: "2px solid white", marginTop: -10 }}
            />
            <div style={{ marginTop: 10 }}>
                <Button icon={<UploadOutlined />} onClick={() => document.getElementById("uploadInput")?.click()}>
                    Upload
                </Button>
                <input
                    id="uploadInput"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleUpload}
                />
            </div>

            < Modal
                open={isCropModalVisible}
                title="Crop Image"
                onCancel={() => setCropModalVisible(false)}
                footer={
                    [
                        <Button key="cancel" onClick={() => setCropModalVisible(false)}>
                            Cancel
                        </Button>,
                        <Button key="crop" type="primary" onClick={cropImage}>
                            Crop
                        </Button>,
                    ]}
            >
                {uploadedImage && (
                    <div style={{ position: "relative", height: 400, width: "100%" }}>
                        <Cropper
                            image={uploadedImage}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                        />
                    </div>
                )}
            </Modal >
        </div >
    );
};

export default ProfileAvatar;
